import { Component, ElementRef, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, skip, switchMap } from 'rxjs/operators';
import { fadeAnimation } from 'src/animations';
import { Contact } from '../../../classes';
import { ContactService } from '../../../services/contact.service';

@Component({
  selector: 'app-select-contact',
  templateUrl: './select-contact.component.html',
  styleUrls: ['./select-contact.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectContactComponent),
      multi: true
    }
  ],
  animations: [ fadeAnimation ]
})
export class SelectContactComponent implements ControlValueAccessor, OnInit {

  @Input() label: string = 'Ansprechpartner';

  currentContact: Contact;

  @Input() placeholder: string = "Ansprechpartner suchen";
  @Input() appearance: string = 'standard';
  
  @Output() focus: EventEmitter<void> = new EventEmitter();
  @Output() selectionChanged: EventEmitter<number> = new EventEmitter();

  @Input() set customerSetted(value: number) {
    if(!value) return;
    this.filterControl.setValue(null);
    this._contactService.getCustomerContacts(value).subscribe(result => this.contacts = result);
  }

  @ViewChild("inputField", { static: true }) inputField: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  private _currentValue = new BehaviorSubject<number>(null);
  currentContact$ = this._currentValue.pipe(switchMap((value: number) => this._contactService.getContactById(value, true)));
  currentLabel$ = this.currentContact$.pipe(map((contact: Contact) => contact? [contact.contactperssalutation, contact.contactpersfname, contact.contactperslname].filter(x => x).join(' '): this.label));

  public contacts: Contact[] = [];

  formGroup: FormGroup;

  private _subscriptions: Subscription[] = [];

  /**
   * the constructor creates a new instance of the select component
   */
  constructor(
    private _formBuilder: FormBuilder,
    private _contactService: ContactService
  ) {
    this.formGroup = this._formBuilder.group({
      'value': null,
      'filter': null
    });
  }
  /**
   * the method returns the display string
   * @param contact 
   * @returns 
   */
  display(contact: Contact): string {
    return [contact.contactperssalutation, contact.contactpersfname, contact.contactperslname].filter(x => x).join(' ');
  }
  /**
   * the method is called on component initialization
   */
  ngOnInit(): void {
    this._subscriptions.push(...[
      this.filterControl.valueChanges
        .pipe(
          skip(1),
          debounceTime(1000),
          distinctUntilChanged()
        ).subscribe((value) => this.sendSearch(value)),
      this.valueControl.valueChanges.subscribe((value: number) => {
        this._currentValue.next(value);
        this.selectionChanged.next(value);
      })
    ]);
  }
  /**
   * the method emits the focus event
   */
  onFocus() {
    this.focus.emit();
  }

  public onTouched: () => void = () => { };
  registerOnTouched = (fn: any) => this.onTouched = fn;

  registerOnChange(fn: any): void {
    this._subscriptions
      .push(this.valueControl.valueChanges.subscribe(fn));
  }

  sendSearch(term: string){
    if(typeof term !== 'string') term = '';

    let normalized = term
      .trim()
      .toLocaleLowerCase();

    this._contactService
      .searchContactsByString(normalized)
      .subscribe(result => this.contacts = result.content as any);
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  selectContact = (event: MatAutocompleteSelectedEvent) => {
    this.valueControl.setValue(event? event.option.value: null);
    this.inputField.nativeElement.blur();
    this.filterControl.reset();
    if (this.autocomplete && this.autocomplete.panelOpen) this.autocomplete.closePanel();
  }

  setContactIfOneLeft() {
    if (this.contacts.length === 1) this.selectContact({ option: { value: this.contacts[0] } } as MatAutocompleteSelectedEvent);
  }

  writeValue = (val: any) => this.valueControl.patchValue(val);

  // controls
  get valueControl(): AbstractControl {
    return this.formGroup.controls['value'];
  }
  get filterControl(): AbstractControl {
    return this.formGroup.controls['filter'];
  }

}
